import { useState, useMemo, useCallback } from 'react';
import { isRequestFailure, useFetch } from './useFetch';

interface User {
  id: number;
  name: string;
  email?: string;
  group?: {
    id: number;
    name: string;
  };
}

const ALLOWED_GROUPS = [1, 2, 3, 6, 7]; // Sistema, Admin, Tutor, Secretaria Municipal, Gestor de Instituição

// Cache global para analytics
const analyticsCache: Map<string, { data: any; timestamp: number }> = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Hook para buscar dados de analytics sob demanda (lazy loading).
 * Não faz requisição automática - apenas quando fetchData() é chamado.
 */
export const useAnalyticsData = (user?: User | null) => {
  const { requestApi } = useFetch('/v1/admin/analytics');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const hasAccess = useMemo(() => {
    if (!user || !user.group?.id) return false;
    return ALLOWED_GROUPS.includes(user.group.id);
  }, [user]);

  /**
   * Função para buscar dados de analytics sob demanda
   * @param endpoint - O endpoint a ser consultado (ex: '/users/overview')
   */
  const fetchData = useCallback(
    async (endpoint: string) => {
      if (!hasAccess || !endpoint) {
        return;
      }

      const cacheKey = `${user?.id}:${endpoint}`;
      const cachedItem = analyticsCache.get(cacheKey);
      const now = Date.now();

      // Verificar cache válido
      if (cachedItem && now - cachedItem.timestamp < CACHE_TTL) {
        setData(cachedItem.data);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setData(null);

        const response = await requestApi('get', undefined, `/v1/admin/analytics${endpoint}`);
        if (isRequestFailure(response)) throw response.error;
        const responseData = response?.data?.data || null;

        // Armazenar em cache
        analyticsCache.set(cacheKey, {
          data: responseData,
          timestamp: now,
        });

        setData(responseData);
      } catch (err: any) {
        setError(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [hasAccess, requestApi, user?.id]
  );

  return { data, loading, hasAccess, error, fetchData };
};
